// Minimal logger: silences chatter in production, forwards warn/error,
// redacts common secret-shaped keys, and gives a single chokepoint to plug in
// an external error monitor (Sentry / Datadog) later.

type Level = 'debug' | 'info' | 'log' | 'warn' | 'error';

const isProd = process.env.NODE_ENV === 'production';
const isDebug = process.env.REACT_APP_DEBUG === 'true';

// Field names that must never appear in logs, at any depth.
const REDACT_KEYS = /^(password|token|access_token|refresh_token|authorization|cookie|session|secret|api_key|apiKey|creditCard|card_number|private_key|jwt|csrf|bearer|x_api_key|x_auth_token|x_access_token)$/i;

// Optional hook so an external monitor (e.g. Sentry, Datadog) can subscribe to
// warn/error events without this module taking a hard dependency on it.
let externalReporter: ((level: Level, args: unknown[]) => void) | null = null;

/**
 * Register (or clear) an external monitor to receive `warn` / `error` events.
 *
 * The logger stays dependency-free; wire this up once at app startup from
 * whatever place owns the Sentry/Datadog client. Pass `null` to detach.
 *
 * @param fn - callback invoked with the level and the already-redacted args,
 *   or `null` to remove the current reporter.
 * @example
 *   setExternalReporter((level, args) => Sentry.captureMessage(String(args[0]), level));
 */
export function setExternalReporter(
  fn: ((level: Level, args: unknown[]) => void) | null,
): void {
  externalReporter = fn;
}

function redact(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value as object)) return '[Circular]';
  seen.add(value as object);
  if (Array.isArray(value)) return value.map((v) => redact(v, seen));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = REDACT_KEYS.test(k) ? '[REDACTED]' : redact(v, seen);
  }
  return out;
}

function emit(level: Level, args: unknown[]): void {
  const safe = args.map((a) => redact(a));
  // eslint-disable-next-line no-console
  console[level](...safe);
  // Hook point: forward warn/error to a monitor when one is wired up.
  if (externalReporter && (level === 'warn' || level === 'error')) {
    try {
      externalReporter(level, safe);
    } catch {
      // Never let the reporter itself break logging.
    }
  }
}

/**
 * Application logger. Prefer this over `console.*` everywhere in the app.
 *
 * Behavior:
 * - `debug` / `info` / `log` are silenced in production unless
 *   `REACT_APP_DEBUG=true` at build time.
 * - `warn` / `error` are always emitted and, if one is registered via
 *   {@link setExternalReporter}, forwarded to an external monitor.
 * - Every argument is deep-scanned and any field whose key looks like a
 *   secret (`password`, `token`, `authorization`, `api_key`, …) is replaced
 *   with `[REDACTED]` before it hits the console or the reporter.
 *
 * @example
 *   logger.info('user signed in', { userId });
 *   logger.error('checkout failed', err);
 */
export const logger = {
  debug: (...a: unknown[]): void => { if (!isProd || isDebug) emit('debug', a); },
  info:  (...a: unknown[]): void => { if (!isProd || isDebug) emit('info', a); },
  log:   (...a: unknown[]): void => { if (!isProd || isDebug) emit('log', a); },
  warn:  (...a: unknown[]): void => emit('warn', a),
  error: (...a: unknown[]): void => emit('error', a),
};
